import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import { exportToCsv } from "../utils/exportCsv";
import { format, parseISO } from "date-fns";

interface Product {
  name: string;
  code: string;
  inStock: boolean;
}

interface CompoundingMaterial {
  id: string;
  product_name: string;
  manufacturer: string;
  lot_number: string;
  expiry_date: string;
  remaining_quantity_grams: number;
  location?: string;
  is_hazardous: boolean;
  status: string;
  notes?: string;
  // New registry fields
  volgnummer?: string;
  pakket_status?: "open" | "verzegeld" | "leeg";
  conformiteit?: boolean;
  autorisatienummer?: string;
  referentie?: string;
  code_grondstof?: string;
  code_specialiteit?: string;
  hoeveelheid?: string;
  leverancier?: string;
  datum_afsluiting?: string;
  openingsdatum?: string;
  invoerdatum?: string;
  aankoopdatum?: string;
}

interface PharmaceuticalSpecialty {
  id: string;
  product_name: string;
  cnk_code?: string | null;
  expiry_date: string;
  number_of_boxes: number;
  batch_number?: string | null;
  location?: string | null;
  handling_method: string;
  status: string;
  is_return_completed: boolean;
  return_completed_date?: string;
  return_completed_by?: string;
  notes?: string | null;
}

interface DisposalSchedule {
  id: string;
  scheduled_date: string;
  disposal_company: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  status: string;
  total_items: number;
  notes?: string;
}

interface ExpiryCheckSession {
  id: string;
  check_date: string;
  total_compounding_materials_checked: number;
  total_specialties_checked: number;
  total_expired_found: number;
  is_completed: boolean;
  session_notes?: string;
}

const ExpiryChecks: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "compounding" | "specialties" | "disposal"
  >("dashboard");
  const [_loading, setLoading] = useState(false);

  // State for compounding materials
  const [compoundingMaterials, setCompoundingMaterials] = useState<
    CompoundingMaterial[]
  >([]);
  const [showAddCompounding, setShowAddCompounding] = useState(false);
  const [newCompounding, setNewCompounding] = useState<
    Partial<CompoundingMaterial>
  >({
    product_name: "",
    manufacturer: "",
    lot_number: "",
    expiry_date: "",
    remaining_quantity_grams: 0,
    location: "",
    is_hazardous: false,
    notes: "",
    volgnummer: "",
    pakket_status: "verzegeld",
    conformiteit: true,
    autorisatienummer: "",
    referentie: "",
    code_grondstof: "",
    code_specialiteit: "",
    hoeveelheid: "",
    leverancier: "",
    datum_afsluiting: "",
    openingsdatum: "",
    invoerdatum: "",
    aankoopdatum: "",
  });

  // State for pharmaceutical specialties
  const [pharmaceuticalSpecialties, setPharmaceuticalSpecialties] = useState<
    PharmaceuticalSpecialty[]
  >([]);
  const [showAddSpecialty, setShowAddSpecialty] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState<
    Partial<PharmaceuticalSpecialty>
  >({
    product_name: "",
    cnk_code: "",
    expiry_date: "",
    number_of_boxes: 0,
    batch_number: "",
    location: "",
    handling_method: "",
    notes: "",
  });

  // Product lookup state
  const [productSuggestions, setProductSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [productsData, setProductsData] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Load products data from public folder
  useEffect(() => {
    const loadProductsData = async () => {
      setProductsLoading(true);
      try {
        const response = await fetch("/products.json");
        if (response.ok) {
          const data = await response.json();
          setProductsData(data);
          console.log(
            `Products database loaded: ${data.length} products available`,
          );
        } else {
          console.warn("Could not load products.json from public folder");
        }
      } catch (error) {
        console.error("Error loading products data:", error);
      } finally {
        setProductsLoading(false);
      }
    };

    loadProductsData();
  }, []);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // State for disposal schedules
  const [disposalSchedules, setDisposalSchedules] = useState<
    DisposalSchedule[]
  >([]);
  const [showAddDisposal, setShowAddDisposal] = useState(false);
  const [newDisposal, setNewDisposal] = useState<Partial<DisposalSchedule>>({
    scheduled_date: "",
    disposal_company: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    notes: "",
  });

  // Compounding filter + bulk-select state
  const [compoundingSearch, setCompoundingSearch] = useState("");
  const [compoundingFilterStatus, setCompoundingFilterStatus] = useState("all");
  const [compoundingFilterPakket, setCompoundingFilterPakket] = useState("all");
  const [compoundingFilterConform, setCompoundingFilterConform] = useState("all");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // State for check sessions
  const [checkSessions, setCheckSessions] = useState<ExpiryCheckSession[]>([]);
  const [_currentSession, _setCurrentSession] =
    useState<ExpiryCheckSession | null>(null);

  // State for summary data
  const [expirySummary, setExpirySummary] = useState({
    total_items: 0,
    expired_items: 0,
    expiring_soon: 0,
    upcoming_items: 0,
    hazardous_expired: 0,
  });

  // State for print view
  const [showPrintView, setShowPrintView] = useState(false);
  const [printData, setPrintData] = useState<CompoundingMaterial[]>([]);
  const [printType, setPrintType] = useState<"hazardous" | "compounding">(
    "hazardous",
  );

  // CSV import state (compounding materials only)
  const [importCompoundingPreview, setImportCompoundingPreview] = useState<Partial<CompoundingMaterial>[]>([]);
  const [importCompoundingErrors, setImportCompoundingErrors] = useState<string[]>([]);
  const [importingCompounding, setImportingCompounding] = useState(false);

  // Parse a CSV string into rows.
  // Processes the whole text character-by-character so that quoted fields
  // containing embedded newlines (as Tabula produces) are handled correctly.
  const parseCsvRows = (text: string): string[][] => {
    const rows: string[][] = [];
    let cells: string[] = [];
    let cell = "";
    let inQuote = false;
    const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    for (let i = 0; i < src.length; i++) {
      const ch = src[i];
      if (ch === '"') {
        if (inQuote && src[i + 1] === '"') { cell += '"'; i++; } // escaped ""
        else { inQuote = !inQuote; }
      } else if (ch === "," && !inQuote) {
        cells.push(cell.replace(/\n/g, " ").trim());
        cell = "";
      } else if (ch === "\n" && !inQuote) {
        cells.push(cell.replace(/\n/g, " ").trim());
        if (cells.some((c) => c !== "")) rows.push(cells);
        cells = [];
        cell = "";
      } else {
        cell += ch;
      }
    }
    // flush last cell/row
    cells.push(cell.replace(/\n/g, " ").trim());
    if (cells.some((c) => c !== "")) rows.push(cells);

    return rows;
  };

  // Parse a Dutch or ISO date string to YYYY-MM-DD
  const parseDateField = (val: string): string | null => {
    if (!val) return null;
    // DD-MM-YYYY or DD/MM/YYYY
    const dutch = val.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dutch) return `${dutch[3]}-${dutch[2].padStart(2, "0")}-${dutch[1].padStart(2, "0")}`;
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    return null;
  };

  const handleImportCompoundingFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCsvRows(text);
      if (rows.length < 2) { setImportCompoundingErrors(["Bestand heeft geen gegevensrijen."]); return; }
      const errors: string[] = [];
      const preview: Partial<CompoundingMaterial>[] = [];
      // Columns match PDF order (Tabula export):
      // volgnummer, naam, status, conformiteit, autorisatienummer, referentie,
      // code_grondstof, code_specialiteit, hoeveelheid, leverancier, fabrikant,
      // lotnummer, vervaldatum, datum_afsluiting, openingsdatum, invoerdatum, aankoopdatum
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        const [
          volgnummer, product_name, status_raw, conformiteit_raw,
          autorisatienummer, referentie, code_grondstof, code_specialiteit,
          hoeveelheid, leverancier, manufacturer, lot_number,
          expiry_raw, datum_afsluiting_raw, openingsdatum_raw, invoerdatum_raw, aankoopdatum_raw
        ] = r;
        if (!product_name) { errors.push(`Rij ${i + 1}: naam ontbreekt`); continue; }
        const expiry_date = parseDateField(expiry_raw);
        if (!expiry_date) { errors.push(`Rij ${i + 1}: ongeldige vervaldatum "${expiry_raw}"`); continue; }
        const pakket_raw = (status_raw || "").toLowerCase().trim();
        // Accept Dutch PDF values: "geopend" → open, "leeg/empty" → leeg, else verzegeld
        const pakket_status = (["open", "geopend"].includes(pakket_raw) ? "open" : pakket_raw === "leeg" || pakket_raw === "empty" ? "leeg" : "verzegeld") as "open" | "verzegeld" | "leeg";
        const conformiteit = !["nee", "false", "0", "niet conform"].includes((conformiteit_raw || "").toLowerCase().trim());
        preview.push({
          volgnummer: volgnummer || "",
          product_name,
          pakket_status,
          conformiteit,
          autorisatienummer: autorisatienummer || "",
          referentie: referentie || "",
          code_grondstof: code_grondstof || "",
          code_specialiteit: code_specialiteit || "",
          hoeveelheid: hoeveelheid || "",
          leverancier: leverancier || "",
          manufacturer: manufacturer || "",
          lot_number: lot_number || "",
          expiry_date,
          datum_afsluiting: parseDateField(datum_afsluiting_raw) || undefined,
          openingsdatum: parseDateField(openingsdatum_raw) || undefined,
          invoerdatum: parseDateField(invoerdatum_raw) || undefined,
          aankoopdatum: parseDateField(aankoopdatum_raw) || undefined,
          is_hazardous: false,
          remaining_quantity_grams: 0,
        });
      }
      setImportCompoundingPreview(preview);
      setImportCompoundingErrors(errors);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const confirmImportCompounding = async () => {
    if (!user || !selectedPharmacy || importCompoundingPreview.length === 0) return;
    setImportingCompounding(true);
    const rows = importCompoundingPreview.map((r) => ({ ...r, pharmacy_id: selectedPharmacy.id, checked_by: user.id }));
    const { error } = await supabase.from("compounding_materials_expiry").insert(rows);
    setImportingCompounding(false);
    if (error) { setImportCompoundingErrors([`Import mislukt: ${error.message}`]); return; }
    setImportCompoundingPreview([]);
    setImportCompoundingErrors([]);
    loadCompoundingMaterials();
    loadExpirySummary();
  };

  const handleExportCompoundingCsv = () => {
    const headers = [
      "Volgnummer", "Naam", "Status", "Conformiteit", "Autorisatienummer",
      "Referentie", "Code Grondstof", "Code Specialiteit", "Hoeveelheid",
      "Leverancier", "Fabrikant", "Lotnummer", "Vervaldatum",
      "Datum afsluiting", "Openingsdatum", "Invoerdatum", "Aankoopdatum",
      "Locatie", "Gevaarlijk", "Opmerkingen",
    ];
    const rows = compoundingMaterials.map((m) => [
      m.volgnummer || "",
      m.product_name,
      m.pakket_status || "",
      m.conformiteit === false ? "Niet conform" : "Conform",
      m.autorisatienummer || "",
      m.referentie || "",
      m.code_grondstof || "",
      m.code_specialiteit || "",
      m.hoeveelheid || "",
      m.leverancier || "",
      m.manufacturer,
      m.lot_number,
      m.expiry_date ? format(parseISO(m.expiry_date), "dd-MM-yyyy") : "",
      m.datum_afsluiting ? format(parseISO(m.datum_afsluiting), "dd-MM-yyyy") : "",
      m.openingsdatum ? format(parseISO(m.openingsdatum), "dd-MM-yyyy") : "",
      m.invoerdatum ? format(parseISO(m.invoerdatum), "dd-MM-yyyy") : "",
      m.aankoopdatum ? format(parseISO(m.aankoopdatum), "dd-MM-yyyy") : "",
      m.location || "",
      m.is_hazardous ? "Ja" : "Nee",
      m.notes || "",
    ]);
    exportToCsv(`grondstoffen_${format(new Date(), "yyyy-MM-dd")}.csv`, headers, rows);
  };

  const handleExportSpecialtiesCsv = () => {
    const headers = [
      "Product",
      "CNK-code",
      "Lotnummer",
      "Vervaldatum",
      "Aantal doosjes",
      "Locatie",
      "Verwerkingswijze",
      "Status",
      "Retour afgerond",
      "Notities",
    ];
    const rows = pharmaceuticalSpecialties.map((s) => [
      s.product_name,
      s.cnk_code || "",
      s.batch_number || "",
      s.expiry_date ? format(parseISO(s.expiry_date), "dd-MM-yyyy") : "",
      s.number_of_boxes ?? "",
      s.location || "",
      s.handling_method,
      s.status,
      s.is_return_completed ? "Ja" : "Nee",
      s.notes || "",
    ]);
    exportToCsv(
      `specialiteiten_${format(new Date(), "yyyy-MM-dd")}.csv`,
      headers,
      rows,
    );
  };

  const specialtyHandlingOptions = [
    {
      value: "wholesaler_return",
      label: "Retour via Groothandel",
    },
    { value: "pharmintouch_return", label: "Retour via Pharmintouch.be" },
    { value: "sales_rep_pickup", label: "Ophalen door Vertegenwoordiger" },
    { value: "immediate_discard", label: "Direct Wegwerpen" },
  ];

  useEffect(() => {
    if (selectedPharmacy) {
      loadData();
    }
  }, [selectedPharmacy]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCompoundingMaterials(),
        loadPharmaceuticalSpecialties(),
        loadDisposalSchedules(),
        loadCheckSessions(),
        loadExpirySummary(),
      ]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompoundingMaterials = async () => {
    const { data, error } = await supabase
      .from("compounding_materials_expiry")
      .select("*")
      .eq("pharmacy_id", selectedPharmacy?.id)
      .order("expiry_date", { ascending: true });

    if (error) {
      console.error("Error loading compounding materials:", error);
      return;
    }

    setCompoundingMaterials(data || []);
  };

  const loadPharmaceuticalSpecialties = async () => {
    const { data, error } = await supabase
      .from("pharmaceutical_specialties_expiry")
      .select("*")
      .eq("pharmacy_id", selectedPharmacy?.id)
      .order("expiry_date", { ascending: true });

    if (error) {
      console.error("Error loading pharmaceutical specialties:", error);
      return;
    }

    setPharmaceuticalSpecialties(data || []);
  };

  const loadDisposalSchedules = async () => {
    const { data, error } = await supabase
      .from("disposal_schedules")
      .select("*")
      .eq("pharmacy_id", selectedPharmacy?.id)
      .order("scheduled_date", { ascending: true });

    if (error) {
      console.error("Error loading disposal schedules:", error);
      return;
    }

    setDisposalSchedules(data || []);
  };

  const loadCheckSessions = async () => {
    const { data, error } = await supabase
      .from("expiry_check_sessions")
      .select("*")
      .eq("pharmacy_id", selectedPharmacy?.id)
      .order("check_date", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error loading check sessions:", error);
      return;
    }

    setCheckSessions(data || []);
  };

  const loadExpirySummary = async () => {
    const { data, error } = await supabase.rpc("get_expiry_summary", {
      pharmacy_uuid: selectedPharmacy?.id,
    });

    if (error) {
      console.error("Error loading expiry summary:", error);
      return;
    }

    if (data && data.length > 0) {
      setExpirySummary(data[0]);
    }
  };

  const addCompoundingMaterial = async () => {
    if (!user || !selectedPharmacy) {
      return;
    }

    const compoundingData = {
      ...newCompounding,
      pharmacy_id: selectedPharmacy.id,
      checked_by: user.id,
      location: newCompounding.location || null,
      notes: newCompounding.notes || null,
    };

    const { error } = await supabase
      .from("compounding_materials_expiry")
      .insert([compoundingData]);

    if (error) {
      console.error("Error adding compounding material:", error);
      return;
    }

    setNewCompounding({
      product_name: "",
      manufacturer: "",
      lot_number: "",
      expiry_date: "",
      remaining_quantity_grams: 0,
      location: "",
      is_hazardous: false,
      notes: "",
    });
    setShowAddCompounding(false);
    loadCompoundingMaterials();
    loadExpirySummary();
  };

  const addPharmaceuticalSpecialty = async () => {
    if (!user || !selectedPharmacy) {
      return;
    }

    // Validate required fields
    // Validate required fields
    if (
      !newSpecialty.product_name ||
      !newSpecialty.expiry_date ||
      !newSpecialty.number_of_boxes ||
      !newSpecialty.handling_method
    ) {
      alert("Vul alle verplichte velden in, inclusief afhandelingsmethode");
      return;
    }

    const specialtyData = {
      ...newSpecialty,
      pharmacy_id: selectedPharmacy.id,
      checked_by: user.id,
      batch_number: newSpecialty.batch_number || null,
      location: newSpecialty.location || null,
      notes: newSpecialty.notes || null,
      cnk_code: newSpecialty.cnk_code || null,
    };

    // Log the data being sent for debugging
    console.log("Attempting to insert specialty data:", specialtyData);
    console.log("Required fields check:");
    console.log("- product_name:", specialtyData.product_name);
    console.log("- expiry_date:", specialtyData.expiry_date);
    console.log("- number_of_boxes:", specialtyData.number_of_boxes);
    console.log("- handling_method:", specialtyData.handling_method);

    const { error } = await supabase
      .from("pharmaceutical_specialties_expiry")
      .insert([specialtyData]);

    if (error) {
      console.error("Error adding pharmaceutical specialty:", error);
      console.error("Error details:", error.message);
      console.error("Error code:", error.code);
      console.error("Data being sent:", specialtyData);
      alert(
        `Database error: ${error.message}\nCode: ${error.code}\nHint: ${error.hint || "None"}`,
      );
      return;
    }

    setNewSpecialty({
      product_name: "",
      cnk_code: "",
      expiry_date: "",
      number_of_boxes: 0,
      batch_number: "",
      location: "",
      handling_method: "",
      notes: "",
    });
    setShowAddSpecialty(false);
    loadPharmaceuticalSpecialties();
    loadExpirySummary();
  };

  const updateSpecialtyHandling = async (
    id: string,
    handlingMethod: string,
  ) => {
    const { error } = await supabase
      .from("pharmaceutical_specialties_expiry")
      .update({
        handling_method: handlingMethod,
      })
      .eq("id", id);

    if (error) {
      console.error("Error updating specialty handling:", error);
      return;
    }

    loadPharmaceuticalSpecialties();
  };

  const updateMaterialStatus = async (
    id: string,
    status: string,
    table: string,
  ) => {
    const { error } = await supabase
      .from(table)
      .update({ status })
      .eq("id", id);

    if (error) {
      console.error("Error updating material status:", error);
      return;
    }

    if (table === "compounding_materials_expiry") {
      loadCompoundingMaterials();
    } else {
      loadPharmaceuticalSpecialties();
    }
    loadExpirySummary();
  };

  const bulkUpdateMaterialStatus = async (status: string) => {
    if (selectedMaterialIds.size === 0) return;
    setBulkUpdating(true);
    const { error } = await supabase
      .from("compounding_materials_expiry")
      .update({ status })
      .in("id", Array.from(selectedMaterialIds));
    setBulkUpdating(false);
    if (error) { console.error("Bulk update error:", error); return; }
    setSelectedMaterialIds(new Set());
    loadCompoundingMaterials();
    loadExpirySummary();
  };

  const createDisposalSchedule = async () => {
    if (!user || !selectedPharmacy) {
      return;
    }

    const { error } = await supabase.from("disposal_schedules").insert([
      {
        ...newDisposal,
        pharmacy_id: selectedPharmacy.id,
        created_by: user.id,
        total_items: 0,
      },
    ]);

    if (error) {
      console.error("Error creating disposal schedule:", error);
      return;
    }

    setNewDisposal({
      scheduled_date: "",
      disposal_company: "",
      contact_person: "",
      contact_phone: "",
      contact_email: "",
      notes: "",
    });
    setShowAddDisposal(false);
    loadDisposalSchedules();
  };

  const generatePrintableList = (
    type: "hazardous" | "compounding" = "hazardous",
  ) => {
    if (type === "hazardous") {
      const expiredHazardousMaterials = compoundingMaterials.filter(
        (material) => material.status === "expired" && material.is_hazardous,
      );
      setPrintData(expiredHazardousMaterials);
    } else {
      setPrintData(compoundingMaterials);
    }
    setPrintType(type);
    setShowPrintView(true);
  };

  const searchProducts = (query: string) => {
    if (!query || query.length < 2) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!productsData || productsData.length === 0 || productsLoading) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const queryLower = query.toLowerCase().trim();

    // Simple search - filter products by name or code
    const filtered = productsData
      .filter((product: Product) => {
        if (!product.name || !product.code) {
          return false;
        }
        const nameMatch = product.name.toLowerCase().includes(queryLower);
        const codeMatch = product.code.toLowerCase().includes(queryLower);
        return nameMatch || codeMatch;
      })
      .slice(0, 10);

    setProductSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  };

  const selectProduct = (product: Product) => {
    setNewSpecialty({
      ...newSpecialty,
      product_name: product.name,
      cnk_code: product.code,
    });
    setShowSuggestions(false);
    setProductSuggestions([]);
  };

  const markReturnCompleted = async (id: string, completed: boolean) => {
    if (!user) {
      return;
    }

    const updateData = completed
      ? {
          is_return_completed: true,
          return_completed_date: new Date().toISOString().split("T")[0],
          return_completed_by: user.id,
        }
      : {
          is_return_completed: false,
          return_completed_date: null,
          return_completed_by: null,
        };

    const { error } = await supabase
      .from("pharmaceutical_specialties_expiry")
      .update(updateData)
      .eq("id", id);

    if (error) {
      console.error("Error updating return completion:", error);
      return;
    }

    loadPharmaceuticalSpecialties();
  };

  const getStatusColor = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysDiff = Math.ceil(
      (expiry.getTime() - today.getTime()) / (1000 * 3600 * 24),
    );

    if (daysDiff < 0) {
      return "text-red-600 bg-red-50";
    }
    if (daysDiff <= 30) {
      return "text-orange-600 bg-orange-50";
    }
    if (daysDiff <= 90) {
      return "text-yellow-600 bg-yellow-50";
    }
    return "text-green-600 bg-green-50";
  };

  if (showPrintView) {
    return (
      <div className="print-view bg-white p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold">
            {printType === "hazardous"
              ? "Gevaarlijk Afval Afvoerlijst"
              : "Grondstoffen Overzicht"}
          </h1>
          <p className="text-gray-600 mt-2">
            Apotheek: {selectedPharmacy?.name}
          </p>
          <p className="text-gray-600">Adres: {selectedPharmacy?.address}</p>
          <p className="text-gray-600">
            Gegenereerd: {new Date().toLocaleDateString()}
          </p>
        </div>

        <table className="w-full border-collapse border border-gray-300 mb-8">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 p-2 text-left">Product</th>
              <th className="border border-gray-300 p-2 text-left">
                Fabrikant
              </th>
              <th className="border border-gray-300 p-2 text-left">
                Lotnummer
              </th>
              <th className="border border-gray-300 p-2 text-left">
                Vervaldatum
              </th>
              <th className="border border-gray-300 p-2 text-left">
                Hoeveelheid (g)
              </th>
              <th className="border border-gray-300 p-2 text-left">Status</th>
              {printType === "hazardous" && (
                <th className="border border-gray-300 p-2 text-left">
                  Gevaarlijk
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {printData.map((material) => (
              <tr key={material.id}>
                <td className="border border-gray-300 p-2">
                  {material.product_name}
                </td>
                <td className="border border-gray-300 p-2">
                  {material.manufacturer}
                </td>
                <td className="border border-gray-300 p-2">
                  {material.lot_number}
                </td>
                <td className="border border-gray-300 p-2">
                  {new Date(material.expiry_date).toLocaleDateString()}
                </td>
                <td className="border border-gray-300 p-2">
                  {material.remaining_quantity_grams}
                </td>
                <td className="border border-gray-300 p-2">
                  {material.status === "expired"
                    ? "Verlopen"
                    : material.status === "active"
                      ? "Actief"
                      : material.status === "near_expiry"
                        ? "Bijna Verlopen"
                        : material.status === "disposed"
                          ? "Afgevoerd"
                          : material.status}
                </td>
                {printType === "hazardous" && (
                  <td className="border border-gray-300 p-2">
                    {material.is_hazardous ? "Ja" : "Nee"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between items-center mt-8">
          <button
            onClick={() => setShowPrintView(false)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Terug
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Afdrukken
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Vervaldatum Beheer</h1>
        <p className="text-gray-600 mt-1">
          Maandelijkse controle van vervaldatums en afvalverwerking
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "dashboard", label: "Dashboard", icon: "📊" },
            { id: "compounding", label: "Grondstoffen", icon: "🧪" },
            {
              id: "specialties",
              label: "Farmaceutische Specialiteiten",
              icon: "💊",
            },
            { id: "disposal", label: "Afvalverwerking", icon: "🗑️" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() =>
                setActiveTab(
                  tab.id as
                    | "dashboard"
                    | "compounding"
                    | "specialties"
                    | "disposal",
                )
              }
              className={`${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <span className="text-2xl">📦</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Totaal Items
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {expirySummary.total_items}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <span className="text-2xl">⚠️</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Verlopen</p>
                  <p className="text-2xl font-bold text-red-600">
                    {expirySummary.expired_items}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <span className="text-2xl">⏰</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Verloopt Binnenkort
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    {expirySummary.expiring_soon}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <span className="text-2xl">☢️</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Gevaarlijk Afval Verlopen
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    {expirySummary.hazardous_expired}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {expirySummary.hazardous_expired > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-red-800">
                    Gevaarlijke Stoffen Moeten Afgevoerd Worden
                  </h3>
                  <p className="text-red-600">
                    U heeft {expirySummary.hazardous_expired} verlopen
                    gevaarlijke stoffen die professioneel afgevoerd moeten
                    worden.
                  </p>
                </div>
                <button
                  onClick={() => generatePrintableList("hazardous")}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Genereer Afvallijst
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Recente Controle Sessies
            </h3>
            <div className="space-y-3">
              {checkSessions.slice(0, 5).map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(session.check_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      {session.total_compounding_materials_checked}{" "}
                      grondstoffen, {session.total_specialties_checked}{" "}
                      specialiteiten gecontroleerd
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {session.total_expired_found} verlopen gevonden
                    </p>
                    <p
                      className={`text-sm ${session.is_completed ? "text-green-600" : "text-orange-600"}`}
                    >
                      {session.is_completed ? "Voltooid" : "In Behandeling"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Compounding Materials Tab */}
      {activeTab === "compounding" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Grondstoffen
            </h2>
            <div className="flex space-x-3">
              <button
                onClick={handleExportCompoundingCsv}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                CSV
              </button>
              <label className="px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 inline-flex items-center gap-2 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" style={{transform:"scaleY(-1)",transformOrigin:"center"}} /><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v13.5" /></svg>
                Importeer CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportCompoundingFile} />
              </label>
              <button
                onClick={() => generatePrintableList("compounding")}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Lijst Afdrukken
              </button>
              <button
                onClick={() => setShowAddCompounding(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Grondstof Toevoegen
              </button>
            </div>
          </div>

          {/* CSV Import Preview — Grondstoffen */}
          {(importCompoundingPreview.length > 0 || importCompoundingErrors.length > 0) && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-indigo-900">
                  CSV import voorbeeld — {importCompoundingPreview.length} rij{importCompoundingPreview.length !== 1 ? "en" : ""} klaar om te importeren
                </h3>
                <button onClick={() => { setImportCompoundingPreview([]); setImportCompoundingErrors([]); }} className="text-indigo-400 hover:text-indigo-600 text-lg leading-none">✕</button>
              </div>
              {importCompoundingErrors.length > 0 && (
                <div className="mb-3 space-y-1">
                  {importCompoundingErrors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">⚠ {e}</p>
                  ))}
                </div>
              )}
              {importCompoundingPreview.length > 0 && (
                <>
                  <div className="overflow-x-auto mb-3">
                    <table className="min-w-full text-xs border border-indigo-200 rounded">
                      <thead className="bg-indigo-100">
                        <tr>{["Volgnr", "Product", "Status", "Conform", "Leverancier", "Fabrikant", "Lot", "Vervaldatum", "Hoeveelheid"].map(h => <th key={h} className="px-2 py-1 text-left font-medium text-indigo-700">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-indigo-100 bg-white">
                        {importCompoundingPreview.map((r, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1">{r.volgnummer}</td>
                            <td className="px-2 py-1 font-medium">{r.product_name}</td>
                            <td className="px-2 py-1">{r.pakket_status}</td>
                            <td className="px-2 py-1">{r.conformiteit ? "✓" : "✗"}</td>
                            <td className="px-2 py-1">{r.leverancier}</td>
                            <td className="px-2 py-1">{r.manufacturer}</td>
                            <td className="px-2 py-1">{r.lot_number}</td>
                            <td className="px-2 py-1">{r.expiry_date}</td>
                            <td className="px-2 py-1">{r.hoeveelheid}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={confirmImportCompounding}
                      disabled={importingCompounding}
                      className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {importingCompounding ? "Importeren..." : `${importCompoundingPreview.length} rijen importeren`}
                    </button>
                    <p className="text-xs text-indigo-600">
                      Kolomvolgorde (PDF/Tabula): Volgnummer, Naam, Status, Conformiteit, Autorisatienummer, Referentie, Code Grondstof, Code Specialiteit, Hoeveelheid, Leverancier, Fabrikant, Lotnummer, Vervaldatum, Datum afsluiting, Openingsdatum, Invoerdatum, Aankoopdatum
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filter bar */}
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Zoek op naam, lot, volgnr..."
              value={compoundingSearch}
              onChange={(e) => setCompoundingSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            <select value={compoundingFilterStatus} onChange={(e) => setCompoundingFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">Alle statussen</option>
              <option value="datum_verlopen">Vervaldatum vóór vandaag</option>
              <option value="active">Actief</option>
              <option value="near_expiry">Bijna verlopen</option>
              <option value="expired">Verlopen</option>
              <option value="disposed">Verwijderd</option>
            </select>
            <select value={compoundingFilterPakket} onChange={(e) => setCompoundingFilterPakket(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">Alle verpakkingen</option>
              <option value="open">Open</option>
              <option value="verzegeld">Verzegeld</option>
              <option value="leeg">Leeg</option>
            </select>
            <select value={compoundingFilterConform} onChange={(e) => setCompoundingFilterConform(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">Conform / niet</option>
              <option value="conform">Conform</option>
              <option value="niet">Niet conform</option>
            </select>
            {(compoundingSearch || compoundingFilterStatus !== "all" || compoundingFilterPakket !== "all" || compoundingFilterConform !== "all") && (
              <button onClick={() => { setCompoundingSearch(""); setCompoundingFilterStatus("all"); setCompoundingFilterPakket("all"); setCompoundingFilterConform("all"); }} className="text-xs text-gray-500 hover:text-gray-700 underline">
                Filters wissen
              </button>
            )}
          </div>

          {/* Bulk action bar */}
          {selectedMaterialIds.size > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-blue-800">{selectedMaterialIds.size} geselecteerd</span>
              <select
                className="text-sm border border-blue-300 rounded px-2 py-1 bg-white"
                defaultValue=""
                onChange={(e) => { if (e.target.value) { bulkUpdateMaterialStatus(e.target.value); e.target.value = ""; } }}
                disabled={bulkUpdating}
              >
                <option value="" disabled>Status wijzigen naar...</option>
                <option value="active">Actief</option>
                <option value="near_expiry">Bijna verlopen</option>
                <option value="expired">Verlopen</option>
                <option value="disposed">Verwijderd</option>
              </select>
              <button onClick={() => setSelectedMaterialIds(new Set())} className="text-xs text-blue-600 hover:text-blue-800 underline">Deselecteren</button>
              {bulkUpdating && <span className="text-xs text-blue-600">Opslaan...</span>}
            </div>
          )}

          {showAddCompounding && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Grondstof Toevoegen
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Row 1: register fields */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Volgnummer</label>
                  <input type="text" placeholder="bijv. 001" value={newCompounding.volgnummer || ""} onChange={(e) => setNewCompounding({ ...newCompounding, volgnummer: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Naam *</label>
                  <input type="text" placeholder="Product naam" value={newCompounding.product_name || ""} onChange={(e) => setNewCompounding({ ...newCompounding, product_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 2: status & conform */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Status verpakking</label>
                  <select value={newCompounding.pakket_status || "verzegeld"} onChange={(e) => setNewCompounding({ ...newCompounding, pakket_status: e.target.value as "open" | "verzegeld" | "leeg" })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="verzegeld">Verzegeld</option>
                    <option value="open">Open</option>
                    <option value="leeg">Leeg</option>
                  </select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Conformiteit</label>
                  <select value={newCompounding.conformiteit ? "true" : "false"} onChange={(e) => setNewCompounding({ ...newCompounding, conformiteit: e.target.value === "true" })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="true">Conform</option>
                    <option value="false">Niet conform</option>
                  </select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Autorisatienummer</label>
                  <input type="text" value={newCompounding.autorisatienummer || ""} onChange={(e) => setNewCompounding({ ...newCompounding, autorisatienummer: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 3: reference & codes */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Referentie (bijv. Ph. Eur.)</label>
                  <input type="text" placeholder="Ph. Eur. / USP / ..." value={newCompounding.referentie || ""} onChange={(e) => setNewCompounding({ ...newCompounding, referentie: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Code grondstof</label>
                  <input type="text" value={newCompounding.code_grondstof || ""} onChange={(e) => setNewCompounding({ ...newCompounding, code_grondstof: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Code specialiteit (CNK)</label>
                  <input type="text" value={newCompounding.code_specialiteit || ""} onChange={(e) => setNewCompounding({ ...newCompounding, code_specialiteit: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 4: supplier & manufacturer & quantity */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Leverancier</label>
                  <input type="text" value={newCompounding.leverancier || ""} onChange={(e) => setNewCompounding({ ...newCompounding, leverancier: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Fabrikant</label>
                  <input type="text" value={newCompounding.manufacturer || ""} onChange={(e) => setNewCompounding({ ...newCompounding, manufacturer: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Hoeveelheid</label>
                  <input type="text" placeholder="bijv. 100g, 250ml" value={newCompounding.hoeveelheid || ""} onChange={(e) => setNewCompounding({ ...newCompounding, hoeveelheid: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 5: lot & expiry */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Lotnummer *</label>
                  <input type="text" value={newCompounding.lot_number || ""} onChange={(e) => setNewCompounding({ ...newCompounding, lot_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Vervaldatum *</label>
                  <input type="date" value={newCompounding.expiry_date || ""} onChange={(e) => setNewCompounding({ ...newCompounding, expiry_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Aankoopdatum</label>
                  <input type="date" value={newCompounding.aankoopdatum || ""} onChange={(e) => setNewCompounding({ ...newCompounding, aankoopdatum: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 6: dates */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Invoerdatum</label>
                  <input type="date" value={newCompounding.invoerdatum || ""} onChange={(e) => setNewCompounding({ ...newCompounding, invoerdatum: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Openingsdatum</label>
                  <input type="date" value={newCompounding.openingsdatum || ""} onChange={(e) => setNewCompounding({ ...newCompounding, openingsdatum: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Datum afsluiting</label>
                  <input type="date" value={newCompounding.datum_afsluiting || ""} onChange={(e) => setNewCompounding({ ...newCompounding, datum_afsluiting: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>

                {/* Row 7: location, hazardous, notes */}
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Locatie</label>
                  <input type="text" value={newCompounding.location || ""} onChange={(e) => setNewCompounding({ ...newCompounding, location: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" /></div>
                <div className="flex items-center pt-5">
                  <input type="checkbox" id="hazardous" checked={newCompounding.is_hazardous || false} onChange={(e) => setNewCompounding({ ...newCompounding, is_hazardous: e.target.checked })} className="rounded border-gray-300 mr-2" />
                  <label htmlFor="hazardous" className="text-sm text-gray-700">Gevaarlijke stof</label>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Opmerkingen</label>
                  <textarea value={newCompounding.notes || ""} onChange={(e) => setNewCompounding({ ...newCompounding, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" rows={2} /></div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowAddCompounding(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={addCompoundingMaterial}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Grondstof Toevoegen
                </button>
              </div>
            </div>
          )}

          {(() => {
            const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
            const filtered = compoundingMaterials
              .filter((m) => {
                if (compoundingSearch) {
                  const q = compoundingSearch.toLowerCase();
                  if (!(m.product_name?.toLowerCase().includes(q) || (m.volgnummer || "").toLowerCase().includes(q) || (m.lot_number || "").toLowerCase().includes(q) || (m.leverancier || "").toLowerCase().includes(q))) return false;
                }
                if (compoundingFilterStatus === "datum_verlopen") {
                  if (!m.expiry_date) return false;
                  if (new Date(m.expiry_date) >= todayMidnight) return false;
                } else if (compoundingFilterStatus !== "all" && m.status !== compoundingFilterStatus) {
                  return false;
                }
                if (compoundingFilterPakket !== "all" && (m.pakket_status || "") !== compoundingFilterPakket) return false;
                if (compoundingFilterConform === "conform" && m.conformiteit === false) return false;
                if (compoundingFilterConform === "niet" && m.conformiteit !== false) return false;
                return true;
              })
              .sort((a, b) => {
                const av = parseInt(a.volgnummer || "0") || 0;
                const bv = parseInt(b.volgnummer || "0") || 0;
                return av - bv;
              });
            const allSelected = filtered.length > 0 && filtered.every((m) => selectedMaterialIds.has(m.id));
            const toggleAll = () => {
              if (allSelected) { setSelectedMaterialIds(new Set()); }
              else { setSelectedMaterialIds(new Set(filtered.map((m) => m.id))); }
            };
            return (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {filtered.length !== compoundingMaterials.length && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
                {filtered.length} van {compoundingMaterials.length} producten weergegeven
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-gray-300" /></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volgnr</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pakket</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverancier / Fabrikant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lot</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vervaldatum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Geopend</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hoeveelheid</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={11} className="px-6 py-8 text-center text-sm text-gray-400">Geen producten gevonden met de huidige filters</td></tr>
                  ) : filtered.map((material) => {
                    const checked = selectedMaterialIds.has(material.id);
                    return (
                    <tr key={material.id} className={checked ? "bg-blue-50" : "hover:bg-gray-50"}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={checked} onChange={() => {
                          const next = new Set(selectedMaterialIds);
                          checked ? next.delete(material.id) : next.add(material.id);
                          setSelectedMaterialIds(next);
                        }} className="rounded border-gray-300" />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{material.volgnummer || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {material.is_hazardous && <span title="Gevaarlijke stof">☢️</span>}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{material.product_name}</p>
                            {material.referentie && <p className="text-xs text-gray-400">{material.referentie}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {material.pakket_status ? (
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            material.pakket_status === "open" ? "bg-blue-100 text-blue-700" :
                            material.pakket_status === "leeg" ? "bg-gray-100 text-gray-500" :
                            "bg-green-100 text-green-700"
                          }`}>
                            {material.pakket_status === "open" ? "Open" : material.pakket_status === "leeg" ? "Leeg" : "Verzegeld"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {material.conformiteit === false ? (
                          <span className="text-red-600 font-medium">Niet conform</span>
                        ) : (
                          <span className="text-green-600">Conform</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <p>{material.leverancier || "—"}</p>
                        <p className="text-xs text-gray-400">{material.manufacturer}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{material.lot_number}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(material.expiry_date)}`}>
                          {new Date(material.expiry_date).toLocaleDateString("nl-BE")}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {material.openingsdatum ? new Date(material.openingsdatum).toLocaleDateString("nl-BE") : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{material.hoeveelheid || (material.remaining_quantity_grams ? `${material.remaining_quantity_grams}g` : "—")}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <select
                          value={material.status}
                          onChange={(e) => updateMaterialStatus(material.id, e.target.value, "compounding_materials_expiry")}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="active">Actief</option>
                          <option value="expired">Verlopen</option>
                          <option value="near_expiry">Bijna Verlopen</option>
                          <option value="disposed">Verwijderd</option>
                        </select>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
            );
          })()}
        </div>
      )}

      {/* Pharmaceutical Specialties Tab */}
      {activeTab === "specialties" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Farmaceutische Specialiteiten
            </h2>
            <div className="flex space-x-3">
              <button
                onClick={handleExportSpecialtiesCsv}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                CSV
              </button>
              <button
                onClick={() => setShowAddSpecialty(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Specialiteit Toevoegen
              </button>
            </div>
          </div>

          {showAddSpecialty && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Farmaceutische Specialiteit Toevoegen
              </h3>
              <div className="bg-blue-50 p-3 rounded mb-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Product Database:{" "}
                  {productsLoading
                    ? "Laden..."
                    : `${productsData?.length || 0} producten beschikbaar`}
                </h4>
                <p className="text-xs text-blue-600">
                  Zoek op productnaam of CNK code - minimaal 2 karakters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative" ref={dropdownRef}>
                  <input
                    type="text"
                    placeholder={
                      productsLoading
                        ? "Producten laden..."
                        : "Product Name (typ om te zoeken)"
                    }
                    value={newSpecialty.product_name || ""}
                    disabled={productsLoading}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewSpecialty({
                        ...newSpecialty,
                        product_name: value,
                      });
                      if (!productsLoading) {
                        searchProducts(value);
                      }
                    }}
                    onFocus={() => {
                      if (
                        !productsLoading &&
                        newSpecialty.product_name &&
                        newSpecialty.product_name.length >= 2
                      ) {
                        searchProducts(newSpecialty.product_name);
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  {showSuggestions && productSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="px-3 py-1 bg-blue-100 text-xs text-blue-700 border-b">
                        {productSuggestions.length} resultaten gevonden
                      </div>
                      {productSuggestions.map((product, index) => (
                        <div
                          key={`${product.code}-${index}`}
                          onClick={() => selectProduct(product)}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-sm">
                            {product.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            CNK: {product.code}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showSuggestions && productSuggestions.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                      <div className="text-sm text-gray-500">
                        Geen producten gevonden voor deze zoekopdracht
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="CNK Code"
                  value={newSpecialty.cnk_code || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      cnk_code: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={newSpecialty.expiry_date || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      expiry_date: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  placeholder="Aantal Dozen"
                  value={newSpecialty.number_of_boxes || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      number_of_boxes: parseInt(e.target.value),
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Batchnummer"
                  value={newSpecialty.batch_number || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      batch_number: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Locatie"
                  value={newSpecialty.location || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      location: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={newSpecialty.handling_method || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      handling_method: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>
                    Selecteer afhandelingsmethode...
                  </option>
                  {specialtyHandlingOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <textarea
                  placeholder="Notes"
                  value={newSpecialty.notes || ""}
                  onChange={(e) =>
                    setNewSpecialty({
                      ...newSpecialty,
                      notes: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowAddSpecialty(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={addPharmaceuticalSpecialty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Specialiteit Toevoegen
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNK Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vervaldatum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dozen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Afhandeling
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Retour Voltooid
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pharmaceuticalSpecialties.map((specialty) => (
                    <tr key={specialty.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {specialty.product_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {specialty.cnk_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(specialty.expiry_date)}`}
                        >
                          {new Date(specialty.expiry_date).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {specialty.number_of_boxes}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={specialty.handling_method || ""}
                          onChange={(e) =>
                            updateSpecialtyHandling(
                              specialty.id,
                              e.target.value,
                            )
                          }
                          className="text-sm border border-gray-300 rounded px-2 py-1 w-full"
                        >
                          <option value="" disabled>
                            Selecteer afhandelingsmethode...
                          </option>
                          {specialtyHandlingOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={specialty.status}
                          onChange={(e) =>
                            updateMaterialStatus(
                              specialty.id,
                              e.target.value,
                              "pharmaceutical_specialties_expiry",
                            )
                          }
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="active">Actief</option>
                          <option value="expired">Verlopen</option>
                          <option value="near_expiry">Bijna Verlopen</option>
                          <option value="disposed">Verwijderd</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={specialty.is_return_completed}
                            onChange={(e) =>
                              markReturnCompleted(
                                specialty.id,
                                e.target.checked,
                              )
                            }
                            className="rounded border-gray-300"
                          />
                          {specialty.is_return_completed && (
                            <span className="text-xs text-green-600">
                              {specialty.return_completed_date &&
                                new Date(
                                  specialty.return_completed_date,
                                ).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Disposal Management Tab */}
      {activeTab === "disposal" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Afvalverwerking
            </h2>
            <button
              onClick={() => setShowAddDisposal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Afvoer Plannen
            </button>
          </div>

          {showAddDisposal && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Afvoer Ophaling Plannen
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="date"
                  value={newDisposal.scheduled_date || ""}
                  onChange={(e) =>
                    setNewDisposal({
                      ...newDisposal,
                      scheduled_date: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Afvalbedrijf"
                  value={newDisposal.disposal_company || ""}
                  onChange={(e) =>
                    setNewDisposal({
                      ...newDisposal,
                      disposal_company: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Contactpersoon"
                  value={newDisposal.contact_person || ""}
                  onChange={(e) =>
                    setNewDisposal({
                      ...newDisposal,
                      contact_person: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Telefoonnummer"
                  value={newDisposal.contact_phone || ""}
                  onChange={(e) =>
                    setNewDisposal({
                      ...newDisposal,
                      contact_phone: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  placeholder="E-mailadres"
                  value={newDisposal.contact_email || ""}
                  onChange={(e) =>
                    setNewDisposal({
                      ...newDisposal,
                      contact_email: e.target.value,
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <textarea
                  placeholder="Notes"
                  value={newDisposal.notes || ""}
                  onChange={(e) =>
                    setNewDisposal({ ...newDisposal, notes: e.target.value })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowAddDisposal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={createDisposalSchedule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Afvoer Plannen
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Geplande Datum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Afvalbedrijf
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {disposalSchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(schedule.scheduled_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {schedule.disposal_company}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div>{schedule.contact_person}</div>
                          <div className="text-gray-500">
                            {schedule.contact_phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {schedule.total_items}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            schedule.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : schedule.status === "scheduled"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {schedule.status === "completed"
                            ? "Voltooid"
                            : schedule.status === "scheduled"
                              ? "Gepland"
                              : schedule.status === "cancelled"
                                ? "Geannuleerd"
                                : schedule.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiryChecks;
